import { useState, useMemo, useCallback } from 'preact/hooks';

/**
 * Configuration for a single filter.
 */
export type FilterConfig = {
	key: string;
	field: keyof MapLocation;
	label: string;
	placeholder: string;
	id: string;
};

/**
 * Active filter state shape. Maps filter keys to arrays of selected values.
 */
export type ActiveFilters = Record< string, string[] >;

/**
 * A single filter option with its label and count of matching locations.
 */
export type FilterOption = {
	value: string;
	label: string;
	count: number;
};

/**
 * A filtered location tuple preserving the original index for dialog lookup.
 */
export type FilteredLocation = {
	location: MapLocation;
	originalIndex: number;
};

/**
 * Build an empty filters object from a config array.
 */
function emptyFilters( configs: FilterConfig[] ): ActiveFilters {
	const filters: ActiveFilters = {};
	configs.forEach( ( c ) => {
		filters[ c.key ] = [];
	} );
	return filters;
}

/**
 * Hook to manage filter state and derive filtered locations.
 *
 * Filtering is "apply on submit" — pending selections are stored separately
 * from active filters. Call `applyFilters()` to commit pending → active.
 * All filters support multi-select (arrays of values).
 *
 * @param  locations     Full array of locations from the REST API.
 * @param  filterConfigs Array of filter configurations describing each filter.
 * @return Filter state, actions, options, and filtered results.
 */
export function useFilters( locations: MapLocation[], filterConfigs: FilterConfig[] ) {
	// Pending select values (not yet applied).
	const [ pending, setPending ] = useState< ActiveFilters >( () => emptyFilters( filterConfigs ) );

	// Applied filters — only these affect the displayed results.
	const [ activeFilters, setActiveFilters ] = useState< ActiveFilters >( () => emptyFilters( filterConfigs ) );

	/**
	 * Set pending values for a specific filter key.
	 */
	const setPendingFilter = useCallback( ( key: string, values: string[] ) => {
		setPending( ( prev ) => ( { ...prev, [ key ]: values } ) );
	}, [] );

	/**
	 * Derive unique, sorted filter options with cross-filtered counts.
	 * When other filters have pending selections, counts reflect only
	 * locations matching those selections — so users see how many results
	 * each option would contribute to.
	 */
	const filterOptions = useMemo( (): Record< string, FilterOption[] > => {
		const result: Record< string, FilterOption[] > = {};

		filterConfigs.forEach( ( config ) => {
			const { key, field } = config;

			// Collect all unique values for this field.
			const allValues = new Set< string >();
			locations.forEach( ( loc ) => {
				const val = loc[ field ];
				if ( val != null && val !== '' ) {
					allValues.add( String( val ) );
				}
			} );

			// Cross-filter: narrow the pool by all OTHER pending filters.
			let pool = locations;
			filterConfigs.forEach( ( otherConfig ) => {
				if ( otherConfig.key === key ) {
					return;
				}
				const otherPending = pending[ otherConfig.key ];
				if ( otherPending && otherPending.length > 0 ) {
					pool = pool.filter( ( loc ) =>
						otherPending.includes( String( loc[ otherConfig.field ] ) )
					);
				}
			} );

			// Count occurrences in the cross-filtered pool.
			const counts = new Map< string, number >();
			allValues.forEach( ( v ) => counts.set( v, 0 ) );

			pool.forEach( ( loc ) => {
				const val = loc[ field ];
				if ( val != null && val !== '' ) {
					const strVal = String( val );
					counts.set( strVal, ( counts.get( strVal ) || 0 ) + 1 );
				}
			} );

			result[ key ] = Array.from( counts.entries() )
				.sort( ( [ a ], [ b ] ) => a.localeCompare( b ) )
				.map( ( [ value, count ] ) => ( {
					value,
					label: `${ value } (${ count })`,
					count,
				} ) );
		} );

		return result;
	}, [ locations, filterConfigs, pending ] );

	/**
	 * Apply pending selections as active filters.
	 */
	const applyFilters = useCallback( () => {
		setActiveFilters( () => {
			const next: ActiveFilters = {};
			filterConfigs.forEach( ( c ) => {
				next[ c.key ] = [ ...( pending[ c.key ] || [] ) ];
			} );
			return next;
		} );
		// Reset comboboxes to placeholder after applying.
		setPending( emptyFilters( filterConfigs ) );
	}, [ filterConfigs, pending ] );

	/**
	 * Remove a single value from an active filter.
	 * Also removes it from the pending selections.
	 */
	const removeFilterValue = useCallback( ( key: string, value: string ) => {
		setActiveFilters( ( prev ) => ( {
			...prev,
			[ key ]: ( prev[ key ] || [] ).filter( ( v ) => v !== value ),
		} ) );
		setPending( ( prev ) => ( {
			...prev,
			[ key ]: ( prev[ key ] || [] ).filter( ( v ) => v !== value ),
		} ) );
	}, [] );

	/**
	 * Clear all active filters and pending selections.
	 */
	const clearAllFilters = useCallback( () => {
		setActiveFilters( emptyFilters( filterConfigs ) );
		setPending( emptyFilters( filterConfigs ) );
	}, [ filterConfigs ] );

	/**
	 * Check if any filter is currently active.
	 */
	const hasActiveFilters = useMemo(
		() => Object.values( activeFilters ).some( ( values ) => values.length > 0 ),
		[ activeFilters ]
	);

	/**
	 * Derive filtered locations from the full array + active filters.
	 * Each entry is a tuple preserving the original index for dialog lookup.
	 * Multi-select uses OR logic within a filter (match any selected value).
	 */
	const filteredLocations = useMemo( (): FilteredLocation[] => {
		return locations
			.map( ( location, index ) => ( { location, originalIndex: index } ) )
			.filter( ( { location } ) => {
				return filterConfigs.every( ( config ) => {
					const active = activeFilters[ config.key ];
					if ( ! active || active.length === 0 ) {
						return true;
					}
					return active.includes( String( location[ config.field ] ) );
				} );
			} );
	}, [ locations, filterConfigs, activeFilters ] );

	return {
		// Filter configs (for rendering).
		filterConfigs,

		// Pending select values.
		pending,
		setPendingFilter,

		// Actions.
		applyFilters,
		removeFilterValue,
		clearAllFilters,

		// Active state.
		activeFilters,
		hasActiveFilters,

		// Filter options (for rendering selects).
		filterOptions,

		// Filtered results.
		filteredLocations,
	};
}
